import { Injectable } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { share } from 'rxjs/operators';
import { IMenu, IMenuChildrenItem } from '../../models/core/menu';
import * as defaults from '../../sidebar-menu-items';
import { AuthService } from '@shared/auth/auth.service';


@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private rolesChanged: Subscription;
  private menu: IMenu[] = [];

  constructor(private authService: AuthService) {
    this.menu = defaults.menu$.value;
    this.rolesChanged = this.authService.rolesChanged$.subscribe(
      () => {
        this.getValidMenuItems();
      }
    );
  }

  // Modify Label of menu item
  modifyLabel(currentValue: string, nextValue: string){
    defaults.menu$.value.forEach(item => {
      if (item.name == currentValue){
        item.name = nextValue;
      }
    });
    // console.log('menu after modify', defaults.menu$.value);
  }

  /** Get all the menu data */
  getValidMenuItems(): Observable<IMenu[]> {
    let filteredMenu: IMenu[] = [];
    this.menu.forEach(item => {
      if (this.showLink(item.auth, item.roles)) {
        filteredMenu.push(item);
      }
    });
    return this.set(filteredMenu);
  }

  showLink(auth?: boolean, roles?: string[]): boolean {
    //Roles required to view link
    if (roles != null && roles.length > 0) {
      return this.authService.isAuthorized(roles);
    }

    //No auth required, show the link
    if (auth == null || !auth) {
      return true
    };

    //Auth required but no roles needed
    return this.authService.isAuthenticated();
  }

  /** Get all the menu data. */
  getAll(): Observable<IMenu[]> {
    return defaults.menu$.asObservable();
  }

  /** Observe the change of menu data. */
  change() {
    return defaults.menu$.pipe(share());
  }

  /** Initialize the menu data. */
  set(menu: IMenu[]): Observable<IMenu[]> {
    defaults.menu$.next(menu);
    return defaults.menu$.asObservable();
  }

  /** Add one item to the menu data. */
  add(menu: IMenu) {
    const tmpMenu = defaults.menu$.value;
    tmpMenu.push(menu);
    defaults.menu$.next(tmpMenu);
  }

  /** Reset the menu data. */
  reset() {
    defaults.menu$.next([]);
  }

  /** Delete empty values and rebuild route. */
  buildRoute(routeArr: string[]): string {
    let route = '';
    routeArr.forEach(item => {
      if (item && item.trim()) {
        route += '/' + item.replace(/^\/+|\/+$/g, '');
      }
    });
    return route;
  }

  /** Get the menu item name based on current route. */
  getItemName(routeArr: string[]): string {
    return this.getLevel(routeArr)[routeArr.length - 1];
  }

  // Whether is a leaf menu
  private isLeafItem(item: IMenuChildrenItem): boolean {
    const cond0 = item.route === undefined;
    const cond1 = item.children === undefined;
    const cond2 = !cond1 && item.children.length === 0;
    return cond0 || cond1 || cond2;
  }

  // Deep clone object could be jsonized
  private deepClone(obj: any): any {
    return JSON.parse(JSON.stringify(obj));
  }

  // Whether two objects could be jsonized equal
  private isJsonObjEqual(obj0: any, obj1: any): boolean {
    return JSON.stringify(obj0) === JSON.stringify(obj1);
  }

  // Whether routeArr equals realRouteArr (after remove empty route element)
  private isRouteEqual(routeArr: Array<string>, realRouteArr: Array<string>): boolean {
    realRouteArr = this.deepClone(realRouteArr);
    realRouteArr = realRouteArr.filter(r => r !== '');
    return this.isJsonObjEqual(routeArr, realRouteArr);
  }

  /** Get the menu level. */
  getLevel(routeArr: string[]): string[] {
    let tmpArr = [];
    defaults.menu$.value.forEach(item => {
      // Breadth-first traverse
      let unhandledLayer = [{ item, parentNamePathList: [], realRouteArr: [] }];
      while (unhandledLayer.length > 0) {
        let nextUnhandledLayer = [];
        for (const ele of unhandledLayer) {
          const eachItem = ele.item;
          const currentNamePathList = this.deepClone(ele.parentNamePathList).concat(eachItem.name);
          const currentRealRouteArr = this.deepClone(ele.realRouteArr).concat(eachItem.route);
          // Compare the full Array for expandable
          if (this.isRouteEqual(routeArr, currentRealRouteArr)) {
            tmpArr = currentNamePathList;
            break;
          }
          if (!this.isLeafItem(eachItem)) {
            const wrappedChildren = eachItem.children.map(child => ({
              item: child,
              parentNamePathList: currentNamePathList,
              realRouteArr: currentRealRouteArr,
            }));
            nextUnhandledLayer = nextUnhandledLayer.concat(wrappedChildren);
          }
        }
        unhandledLayer = nextUnhandledLayer;
      }
    });
    return tmpArr;
  }

  /** Add namespace for translation. */
  addNamespace(menu: IMenu[] | IMenuChildrenItem[], namespace: string) {
    menu.forEach(menuItem => {
      menuItem.name = `${namespace}.${menuItem.name}`;
      if (menuItem.children && menuItem.children.length > 0) {
        this.addNamespace(menuItem.children, menuItem.name);
      }
    });
  }

  ngOnDestroy() {
    this.rolesChanged.unsubscribe();
  }

  updateMenu(): void {
    this.modifyLabel('Login','Lab Listing');

    let temp: IMenu = { icon:'assessment', name:'Reports',  route:'reports', type:'link' };
    this.add(temp);

  }
}
